<script setup lang="ts">
import { computed, ref } from 'vue';
import Dialog from 'primevue/dialog';
import Checkbox from 'primevue/checkbox';
import ProgressSpinner from 'primevue/progressspinner';
import { CustomButton, ButtonType } from '@/components';

defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  isLoading: {
    type: Boolean
  }
});

const strings = {
  header: 'Confirm delete selected data export',
  description: `You're about to permanently delete selected data export.`,
  alertAck: 'Please acknowledge what will happen:',
  check1: 'The selected data export will no longer exist in Studypages Data',
  loaderText: 'Deleting selected data export...'
};

const checkFirst = ref<boolean>(false);

const enableDelete = computed(
  () => checkFirst.value
);

const checkChange = (check: number) => {
  if (check === 1) {
    checkFirst.value = !checkFirst.value;
  }
};

const emits = defineEmits(['onClose', 'onDelete']);

const closeDialog = () => {
  emits('onClose');
};

const deleteExports = () => {
  emits('onDelete');
};
</script>

<template>
  <div>
    <Dialog :visible="visible"
      modal
      class="dialog"
      append-to="self"
      @update:visible="closeDialog">
      <template #container="{}">
        <div class="content-root">
          <div>
            <h3>{{ strings.header }}</h3>
          </div>
          <div class="description">{{ strings.description }}</div>

          <div class="alert-ack">{{ strings.alertAck }}</div>
          <div class="select" @click.prevent="checkChange(1)" v-ripple>
            <div>
              {{ strings.check1 }}
            </div>
            <Checkbox v-model="checkFirst" :binary="true" />
          </div>
          <div class="alert-buttons">
            <div class="buttons-left">
              <CustomButton text="Delete permanently"
                :disabled="!enableDelete"
                :type="ButtonType.NEGATIVE_OUTLINED"
                @onClick="deleteExports"/>
              <CustomButton text="Cancel"
                :type="ButtonType.OUTLINED_PRIMARY"
                @onClick="closeDialog"
                :marginLeft="16"/>
            </div>
          </div>
        </div>
        <div class="content-loading" v-if="isLoading">
          <ProgressSpinner strokeWidth="4" />
          <div>{{ strings.loaderText }}</div>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<style scoped lang="scss">
@use "../../assets/main.scss" as *;
@use "../../assets/dialog" as *;
.content-root {
  width: 600px;
  padding: 40px 28px;
}
h3 {
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
  margin-bottom: 8px;
  padding: 0 12px;
}
.description {
  padding: 10px 12px;
  font-weight: 600;
}
.alert-ack {
  margin-top: 40px;
  padding: 10px 12px;
  font-weight: 600;
}
.select {
  display: flex;
  justify-content: space-between;
  padding: 12px;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background-color: $hover-background;
  }
}
.alert-buttons {
  margin-top: 32px;
  padding: 0 12px;
  display: flex;
  justify-content: flex-end;
}
.buttons-left {
  display: flex;
}
.content-loading {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
